import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../state/authStore";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

/**
 * Auth-gate for protected routes. Splash rendering during boot now lives in
 * `<AuthBootstrap>` (one level above the Router), so this component just
 * needs to handle the post-boot states: guest → redirect; authed → render
 * children; missing admin role → bounce to home.
 *
 * If status is still "checking" we render nothing here — AuthBootstrap's
 * fixed-position splash is on top anyway, and the cookie roundtrip resolves
 * in milliseconds. The hold time is enforced by AuthBootstrap's useBootHold.
 */
export function ProtectedRoute({ children, requireAdmin }: ProtectedRouteProps) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (status === "checking") {
    // AuthBootstrap is rendering the splash over the viewport — render
    // nothing here so we don't paint a half-state behind the splash.
    return null;
  }

  if (status === "guest" || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
